/*
  Ever Leaf Image Renderer — POC (Stage 2A: grayscale, Stage 2B: architecture
  scaffold only). Pure image-processing logic — no DOM wiring, no event
  listeners, no knowledge of Mini/FilePond at all. It only ever touches the
  Canvas elements it's explicitly given. Kept deliberately independent of
  ever-leaf-image-renderer-poc.js (the UI/capture wiring layer) so this
  module can be reused unchanged by the real personalizer later.

  Exposed on window.EverLeafImageRenderer.
*/
(function () {
  'use strict';

  if (window.EverLeafImageRenderer) return;

  /*
    Decodes a File/Blob into a drawable source. Prefers createImageBitmap
    (off-main-thread decode, no object-URL bookkeeping) with a graceful
    fallback to the classic <img> + object-URL approach for browsers/cases
    where createImageBitmap on a File isn't available or fails (see the
    "desktop/mobile browser concerns" note in the POC report).
  */
  async function decodeFileToSource(file) {
    if (typeof createImageBitmap === 'function') {
      try {
        return await createImageBitmap(file);
      } catch (error) {
        // Fall through to the <img> fallback below.
      }
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('EverLeafImageRenderer: could not decode image file.'));
      };
      img.src = url;
    });
  }

  function drawSourceToCanvas(source, canvas) {
    const width = source.width || source.naturalWidth;
    const height = source.height || source.naturalHeight;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(source, 0, 0, width, height);
    return ctx;
  }

  function releaseSource(source) {
    // ImageBitmap sources hold decoded pixel memory until explicitly closed;
    // the <img> fallback path has nothing to release here.
    if (typeof source.close === 'function') source.close();
  }

  /*
    Decodes `file` and draws it into `canvas` completely unmodified. Never
    reads from or mutates anything on `file` itself — only ever writes to
    the destination canvas's own pixel buffer.
  */
  async function renderOriginal(file, canvas) {
    const source = await decodeFileToSource(file);
    drawSourceToCanvas(source, canvas);
    releaseSource(source);
    return canvas;
  }

  /*
    Decodes `file`, draws it into `canvas`, and (by default) converts the
    canvas's own pixels to true luminance-weighted grayscale in place —
    ITU-R BT.601 coefficients (0.299R + 0.587G + 0.114B), not a naive
    per-channel average. `file` itself is never touched. Pass
    { grayscale: false } to just draw it plain (useful for the "Original"
    pane so both panes go through the exact same decode path).
  */
  async function renderCustomerImage(file, canvas, options) {
    options = options || {};
    const source = await decodeFileToSource(file);
    const ctx = drawSourceToCanvas(source, canvas);
    releaseSource(source);

    if (options.grayscale !== false) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        data[i] = gray;
        data[i + 1] = gray;
        data[i + 2] = gray;
        // data[i + 3] (alpha) intentionally untouched.
      }
      ctx.putImageData(imageData, 0, 0);
    }

    return canvas;
  }

  /*
    Stage 2B — Leaf Close-up layered renderer.

    Local composition space: 1060x1060, matching the PSD
    (Images/PRODUCT/Leaf_Layers_01.psd) this pipeline was built against. This
    is the CLOSE-UP local space only — a later, separate full-lamp scene will
    be a different, larger coordinate space that reuses this same
    personalization state, not this constant.
  */
  var LEAF_LOCAL_SIZE = 1060;

  /*
    Text canonical reference, re-inspected directly from the current PSD
    (Text layer: bbox (431,833)-(628,885) on the 1060x1060 canvas, Overlay
    blend, 80% opacity, bold). Font: the PSD's FontSet lists 'Rubik-Bold' for
    Latin script and 'MyriadHebrew-Regular' for Hebrew script. Neither is
    loaded in this theme — Myriad is an Adobe-licensed font we can't
    distribute, and this project deliberately moved to System UI/Sans Serif
    theme-wide (see PROJECT-STATE-HANDOFF.md) rather than a custom webfont.
    Rubik itself is a free, Hebrew-capable Google Font and would be the
    closest real match if a webfont is wanted later, but loading a new font
    is a theme-wide typography decision outside this internal calibration
    phase's scope — deliberately NOT added silently. Using a bold system
    sans-serif stack instead: every OS in the stack has solid Hebrew
    coverage, and it matches the theme's own already-established direction.
  */
  /*
    PASS 3A FINAL DEDICATION POSITIONING MODEL — retires the "Group Y +
    Line Gap" shared-anchor layout (itself a retirement of the even older
    length-based Short/Medium/Long size-BAND system) in favor of fully
    INDEPENDENT per-line Y positions: no shared vertical anchor, no derived
    gap, no automatic midpoint/symmetric math anywhere in this file.
    TEXT_CANONICAL below is the single source of PSD/current-scene-
    calibrated defaults for position (centerXRatio, plus the three Y
    ratios below — all expressed in whatever space drawDedicationLayer
    itself draws in, which is Scene Space in production; see that
    function's own header for the coordinate-space note) and per-line size
    (line1FontSize/line2FontSize/minFontSize, all raw 1060/Leaf-Local-Space
    px — same convention the old shortSize/mediumSize/minSize used).

    Y model (Section 4-9/15 of the brief):
    - line1YSingleRatio — Line 1's position (from TOP) when Line 2 is
      empty. Derived from the exact same PSD Text-layer measurement the
      original single-line centerYRatio used ((833+885)/2/1060) — a
      single-line Dedication still looks IDENTICAL to every prior pass by
      construction, not by coincidence.
    - line1YTwoRatio — Line 1's position (from TOP) when Line 2 also has
      content. Derived from the last pass's own already-approved
      Group-Y(80%)/Line-Gap(10%) geometry: 80% - 10%/2 = 75% — the exact
      same visual position the old model produced for the upper line, just
      now stored directly instead of computed from two other numbers.
    - line2YRatio — Line 2's position, from TOP. The merchant-facing
      control is intentionally anchored from the BOTTOM instead (Section
      7: the merchant places the top line relative to the top, the bottom
      line relative to the bottom, independently) — that from-bottom value
      is converted to this from-top ratio exactly once, upstream, in
      ever-leaf-personalizer.js's sceneTextLayerSettings(), before this
      file ever sees it (Section 15). This constant's own value is derived
      the same way: old two-line lower position was 80% + 10%/2 = 85% from
      top.
    None of these three values are ever derived from each other, from font
    size, or from a shared anchor — each is authoritative on its own
    (Section 9).
  */
  var TEXT_CANONICAL = {
    centerXRatio: (431 + 628) / 2 / LEAF_LOCAL_SIZE, // ~0.4995 — confirms PSD text is horizontally centered on the leaf
    line1YSingleRatio: (833 + 885) / 2 / LEAF_LOCAL_SIZE, // ~0.8104
    line1YTwoRatio: (833 + 885) / 2 / LEAF_LOCAL_SIZE - 0.03, // ~0.7804 — matches the prior model's 80%-10%/2 upper-line geometry
    // From-TOP ratio (the space this function/TEXT_CANONICAL actually
    // draws in) — matches the prior model's 80%+10%/2 = 85%-from-top
    // lower-line geometry. The merchant-facing "from bottom" conversion
    // (1 - fromBottomRatio) happens exactly once, upstream, in
    // ever-leaf-personalizer.js's sceneTextLayerSettings() — see this
    // function's own header comment.
    line2YRatio: (833 + 885) / 2 / LEAF_LOCAL_SIZE + 0.03, // ~0.8404
    line1FontSize: 52, // raw 1060-space px — short names, largest canonical size (same value the old shortSize default used)
    line2FontSize: 42,
    minFontSize: 22, // absolute floor — auto-fit never shrinks a line below this regardless of length
    maxWidthRatio: 0.55, // allowed per-line fitting box — wider than the PSD sample's own tight bbox, since real customer names vary; see report
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Arial, sans-serif',
    fontWeight: 'bold',
    color: '#ffffff',
    opacity: 0.8,
    blendMode: 'overlay'
  };

  // FINAL INTERACTIVE PERFORMANCE PASS — shared measurement canvas/context
  // for computeDedicationLineFit() below, created lazily on first use (never
  // at module load, so this file has no side effect if it's ever loaded in
  // a context with no `document`). 1x1 is sufficient — measureText() does
  // not depend on the canvas's own backing-store size.
  var dedicationMeasureCtx = null;
  function getDedicationMeasureContext() {
    if (!dedicationMeasureCtx) {
      dedicationMeasureCtx = document.createElement('canvas').getContext('2d');
    }
    return dedicationMeasureCtx;
  }

  /*
    Per-line auto-fit (Section 20 of the two-line brief) — sibling to the
    old computeTextFit(), but starts DIRECTLY from `configuredFontSize`
    (raw 1060-space px) instead of a length-based band: each line has its
    OWN configured size and shrinks independently, never enlarging past it
    (`actualSize <= configuredSize`), never shrinking below `minFontSize`.
    Does ONE-to-three measureText passes, same as before — call only when
    a line's text or its configured/min size actually changes, never per
    drag/zoom frame; the caller is responsible for caching the result
    (layerSettings.text.line1FontSizeRatio/line2FontSizeRatio).
  */
  function computeDedicationLineFit(text, configuredFontSize, minFontSize, options) {
    options = Object.assign({}, TEXT_CANONICAL, options || {});
    var configuredPx = configuredFontSize != null ? configuredFontSize : TEXT_CANONICAL.line1FontSize;
    var minPx = minFontSize != null ? minFontSize : TEXT_CANONICAL.minFontSize;
    if (!text) return { fontSizeRatio: configuredPx / LEAF_LOCAL_SIZE, widthPx1060: 0, shrunk: false };

    // FINAL INTERACTIVE PERFORMANCE PASS — reuse ONE shared, lazily-created
    // 1x1 measurement canvas/context (module-scope, below) instead of
    // creating a fresh canvas element + 2D context on every call. This
    // function runs twice per keystroke (Line 1 and Line 2, via
    // refitDedicationLines()), so a per-call canvas allocation was real,
    // if secondary, overhead on every character typed. `ctx.font`/
    // `ctx.direction` are always set fresh below before any measureText()
    // call, so a shared context reads back identical metrics every time —
    // this changes nothing about the computed fit, only how the
    // measurement context is obtained.
    var ctx = getDedicationMeasureContext();
    var maxWidthPx = options.maxWidthRatio * LEAF_LOCAL_SIZE;
    // PASS 3A FINAL UX PASS, Section 27 — measure with the SAME direction
    // semantics drawDedicationLayer() actually draws with (Hebrew vs.
    // English/mixed), not just the same font/weight. Canvas text-metric
    // shaping can differ by `direction` for complex/bidi scripts, so a
    // measurement taken without it is not guaranteed representative of the
    // real rendered width for Hebrew text.
    ctx.direction = detectTextDirection(text);

    ctx.font = options.fontWeight + ' ' + configuredPx + 'px ' + options.fontFamily;
    var width = ctx.measureText(text).width;

    if (width <= maxWidthPx || configuredPx <= minPx) {
      return { fontSizeRatio: configuredPx / LEAF_LOCAL_SIZE, widthPx1060: width, shrunk: false };
    }

    // Font metrics scale ~linearly with size for a fixed string — one
    // proportional adjustment, then a verification measurement (glyph
    // hinting/rounding means it's not perfectly linear at small sizes).
    var scaledPx = Math.max(minPx, configuredPx * (maxWidthPx / width));
    ctx.font = options.fontWeight + ' ' + scaledPx + 'px ' + options.fontFamily;
    var verifiedWidth = ctx.measureText(text).width;
    if (verifiedWidth > maxWidthPx && scaledPx > minPx) {
      scaledPx = Math.max(minPx, scaledPx * (maxWidthPx / verifiedWidth));
      ctx.font = options.fontWeight + ' ' + scaledPx + 'px ' + options.fontFamily;
      verifiedWidth = ctx.measureText(text).width;
    }

    return { fontSizeRatio: scaledPx / LEAF_LOCAL_SIZE, widthPx1060: verifiedWidth, shrunk: true };
  }

  var DEFAULT_LAYER_SETTINGS = {
    back: { visible: true, opacity: 1 },
    leafBase: { visible: true, opacity: 1 },
    photo: { visible: true, opacity: 1 },
    text: {
      visible: true,
      opacity: TEXT_CANONICAL.opacity,
      blendMode: TEXT_CANONICAL.blendMode,
      color: TEXT_CANONICAL.color,
      fontFamily: TEXT_CANONICAL.fontFamily,
      fontWeight: TEXT_CANONICAL.fontWeight
    },
    light: { visible: true, opacity: 0.471, blendMode: 'overlay' }
  };

  /*
    PASS 3C — default static lamp-layer weights (Section 20 of the brief).
    Matches the Close-Up endpoint exactly (Blur fully visible, Sharp
    hidden) — used only as a fallback when a caller omits `lampWeights`
    entirely, so any call site that predates Pass 3C keeps rendering
    byte-identically. Real callers (ever-leaf-personalizer.js) always pass
    the live, continuously-varying weights from getLampLayerWeights().
  */
  var DEFAULT_LAMP_WEIGHTS = { blurWeight: 1, sharpWeight: 0 };

  /*
    Default starting photo transform, calibrated from the PSD's own sample
    "Photo" layer (offset -61,204, size 1233x898 on the 1060x1060 canvas) —
    not an arbitrary guess. Normalized so it's independent of whatever pixel
    size the canvas is actually rendered at.
  */
  var DEFAULT_PHOTO_TRANSFORM = {
    x: -61 / LEAF_LOCAL_SIZE,
    y: 204 / LEAF_LOCAL_SIZE,
    scale: 1233 / LEAF_LOCAL_SIZE
  };
  // Pass 3A interaction-model correction — the SAME calibration reference
  // as DEFAULT_PHOTO_TRANSFORM above, expressed in the new canonical
  // Center convention (PSD sample Photo: offset -61,204, size 1233x898 on
  // the 1060 canvas -> center = top-left + half the drawn width/height).
  // This is the bootstrap default used before any real photo/state exists
  // — the single source of truth ever-leaf-personalizer.js merges partial
  // Center-space state against, exactly like DEFAULT_PHOTO_TRANSFORM was
  // before.
  var DEFAULT_PHOTO_TRANSFORM_CENTER = {
    centerX: -61 / LEAF_LOCAL_SIZE + 1233 / LEAF_LOCAL_SIZE / 2,
    centerY: 204 / LEAF_LOCAL_SIZE + 898 / LEAF_LOCAL_SIZE / 2,
    scale: 1233 / LEAF_LOCAL_SIZE
  };

  /*
    crossOrigin='anonymous' is REQUIRED here. Without it, drawing these
    Shopify-CDN-hosted images onto a canvas marks that canvas "not
    origin-clean" per the HTML spec — even though Shopify's CDN itself sends
    permissive CORS headers — because the browser only requests/consumes
    those headers when the <img> is explicitly put in CORS mode first. Once a
    canvas is tainted this way, any getImageData()/putImageData() call on it
    throws SecurityError. This was the actual root cause of the customer
    photo silently failing to appear: buildMaskAlphaCanvas() (below) draws
    Photo_Mask.png then immediately calls getImageData() on the very same
    canvas — with an untainted <img> that throws every single time a photo
    render was attempted, aborting the function before the photo, mask, Text,
    or Light layers ever reached the visible canvas. Confirmed by tracing the
    exact call order: drawBack/drawLeafBase (no pixel readback, so no
    exception) ran fine, then the very next pixel-readback call in the
    pipeline is buildMaskAlphaCanvas's getImageData — matching exactly what
    was visually observed (leaf renders, nothing after it does).
  */
  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('EverLeafImageRenderer: failed to load asset ' + url));
      };
      img.src = url;
    });
  }

  /*
    Scans a mask image's luminance for its visible ("photo window") bounding
    box, normalized to 0..1. Used to fit an uploaded photo of ANY aspect
    ratio against the mask's actual shape/position instead of hardcoding the
    PSD sample Photo's literal pixel size as if it applied to every upload.
  */
  function computeMaskBounds(maskImage, canvasSize) {
    var canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(maskImage, 0, 0, canvasSize, canvasSize);
    var data = ctx.getImageData(0, 0, canvasSize, canvasSize).data;
    var threshold = 20; // ignore near-black noise/anti-aliasing outside the window
    var minX = canvasSize, minY = canvasSize, maxX = 0, maxY = 0, found = false;
    for (var y = 0; y < canvasSize; y++) {
      for (var x = 0; x < canvasSize; x++) {
        var i = (y * canvasSize + x) * 4;
        var luminance = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        if (luminance > threshold) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) {
      // Degenerate mask (all black) — fall back to the full canvas so the
      // photo still has somewhere sensible to sit rather than a zero-size box.
      return { left: 0, top: 0, right: 1, bottom: 1, width: 1, height: 1, centerX: 0.5, centerY: 0.5 };
    }
    var left = minX / canvasSize;
    var top = minY / canvasSize;
    var right = (maxX + 1) / canvasSize;
    var bottom = (maxY + 1) / canvasSize;
    return {
      left: left,
      top: top,
      right: right,
      bottom: bottom,
      width: right - left,
      height: bottom - top,
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2
    };
  }

  // Pass 3A targeted correction — Scene Asset descriptor-fingerprint cache.
  // Previously a plain cache-FOREVER singleton (`if (!assetsPromise)`),
  // which meant a genuinely-changed Scene Descriptor (a merchant picking a
  // new, validated Content image in the Visualizer Scene Assets block —
  // see sections/ever-leaf-product.liquid) was silently ignored on a Theme
  // Editor section reload: init() re-calls this with the NEW image
  // sources, but the stale FIRST-ever-built bundle kept winning forever.
  // Now keyed by a fingerprint of the actual inputs (leafBase/photoMask/
  // light source identity) — an unchanged fingerprint still short-circuits
  // to the cached bundle (no redundant rebuild on an ordinary reload with
  // no Scene Asset change), but a changed fingerprint rebuilds. Rebuilding
  // this bundle never touches customer File/Center/Scale/Dedication (those
  // live entirely in ever-leaf-personalizer.js's state, untouched here).
  var assetsCache = null; // { fingerprint, promise }
  function buildAssetsFingerprint(leafBaseKey, photoMaskKey, lightKey) {
    return String(leafBaseKey) + '|' + String(photoMaskKey) + '|' + String(lightKey);
  }
  // Shared by both loaders below — derives the static, transform-independent
  // work built from the three Leaf Local Space images (mask luminance-alpha
  // stencil + its bounding box), identically regardless of whether those
  // images came from raw asset URLs or a pre-cropped 2048 scene (Pass 3A).
  function buildAssetsBundle(leafBase, photoMask, light) {
    return {
      leafBase: leafBase,
      photoMask: photoMask,
      light: light,
      maskAlpha: buildMaskAlphaCanvas(photoMask, LEAF_LOCAL_SIZE),
      maskBounds: computeMaskBounds(photoMask, LEAF_LOCAL_SIZE)
    };
  }

  /*
    Loads (and caches, per descriptor fingerprint) the three fixed close-up
    assets exported from the PSD, plus derives the static, transform-
    independent work built from them: the mask's luminance-alpha stencil and
    its bounding box. None of this depends on the customer's photo or its
    transform, so it's computed once per distinct set of source URLs — never
    rebuilt per render/pointermove, and never rebuilt on a reload that
    didn't actually change any Scene Asset URL.
    `urls` is { leafBase, photoMask, light }.
  */
  function loadPersonalizerAssets(urls) {
    var fingerprint = buildAssetsFingerprint(urls.leafBase, urls.photoMask, urls.light);
    if (!assetsCache || assetsCache.fingerprint !== fingerprint) {
      assetsCache = {
        fingerprint: fingerprint,
        promise: Promise.all([
          loadImage(urls.leafBase),
          loadImage(urls.photoMask),
          loadImage(urls.light)
        ]).then(function (results) {
          return buildAssetsBundle(results[0], results[1], results[2]);
        })
      };
    }
    return assetsCache.promise;
  }

  /*
    Pass 3A — sibling to loadPersonalizerAssets() for callers that already
    have ready-to-use Leaf Local Space image sources (e.g. ever-leaf-lamp-
    scene.js's pre-cropped 2048 scene canvases, itself now also fingerprint-
    cached per URL — see that file's loadImageCached()) instead of three raw
    asset URLs to fetch. `images` is { leafBase, photoMask, light } —
    already-loaded Image/Canvas elements; each source's own `.src` (or, for
    a plain <canvas> with no `.src`, its object identity) is the fingerprint
    key. Same cache slot/contract as loadPersonalizerAssets() (only one of
    the two loaders is used per page load in practice).
  */
  function loadPersonalizerAssetsFromImages(images) {
    var fingerprint = buildAssetsFingerprint(
      images.leafBase && (images.leafBase.src || images.leafBase),
      images.photoMask && (images.photoMask.src || images.photoMask),
      images.light && (images.light.src || images.light)
    );
    if (!assetsCache || assetsCache.fingerprint !== fingerprint) {
      assetsCache = {
        fingerprint: fingerprint,
        promise: Promise.resolve(buildAssetsBundle(images.leafBase, images.photoMask, images.light))
      };
    }
    return assetsCache.promise;
  }

  /*
    Computes a normalized { x, y, scale } starting transform (same
    top-left-x/top-left-y/width-ratio convention as everywhere else in this
    file — see renderPersonalizedLeaf's doc comment) that COVERS the mask's
    bounding box with the photo's own aspect ratio preserved, centered on the
    mask — i.e. a "background-size: cover" fit against the photo window, not
    a hardcoded literal size. This replaces relying on the PSD sample
    Photo's literal 1233x898 dimensions for every upload (that figure is a
    calibration reference only, per spec — still exposed as
    DEFAULT_PHOTO_TRANSFORM for cases with no photo/no mask bounds yet).
  */
  function computeDefaultPhotoTransform(photoWidth, photoHeight, maskBounds, canvasSize) {
    canvasSize = canvasSize || LEAF_LOCAL_SIZE;
    var maskPxWidth = maskBounds.width * canvasSize;
    var maskPxHeight = maskBounds.height * canvasSize;
    var coverScale = Math.max(maskPxWidth / photoWidth, maskPxHeight / photoHeight);
    var drawWidth = photoWidth * coverScale;
    var drawHeight = photoHeight * coverScale;
    var centerXPx = maskBounds.centerX * canvasSize;
    var centerYPx = maskBounds.centerY * canvasSize;
    return {
      x: (centerXPx - drawWidth / 2) / canvasSize,
      y: (centerYPx - drawHeight / 2) / canvasSize,
      scale: drawWidth / canvasSize
    };
  }

  /*
    Pass 3A interaction-model correction — the OLD clampScaleOnly()/
    clampPhotoTransform() (mask-cover-guarantee position clamp + automatic/
    manual position modes + scale-pivot coupling) are RETIRED from the
    active interaction path. They made scale changes able to shift position
    and position changes able to interact with scale limits through shared
    mask-relative geometry — the user-observed "drift/bounce near scale
    min/max" was this coupling, not a bug in any one formula. The
    replacement model makes Scale and Center two COMPLETELY independent
    degrees of freedom, each clamped against its own simple explicit
    merchant-configured bounds, with NO mask geometry, NO scale pivot, and
    NO automatic "always cover" correction anywhere in this path:
      - clampScaleValue() only ever looks at scale in, scale out.
      - clampCenterValue() only ever looks at center in, center out.
    computeDefaultPhotoTransform() above (mask-cover fit, used ONLY for
    picking a good INITIAL scale/position at upload/Reset time) is
    unchanged and still valuable — it is not a per-frame clamp, just a
    one-time starting-point calculation, converted to Center via
    topLeftToCenter() by the caller.
  */
  function clampScaleValue(scale, limits) {
    limits = limits || {};
    var minScale = typeof limits.scaleMin === 'number' ? limits.scaleMin : 0.2;
    var maxScale = typeof limits.scaleMax === 'number' ? limits.scaleMax : 4;
    if (minScale > maxScale) minScale = maxScale;
    return Math.max(minScale, Math.min(maxScale, scale));
  }

  function clampCenterValue(center, limits) {
    limits = limits || {};
    var minX = typeof limits.centerMinX === 'number' ? limits.centerMinX : -Infinity;
    var maxX = typeof limits.centerMaxX === 'number' ? limits.centerMaxX : Infinity;
    var minY = typeof limits.centerMinY === 'number' ? limits.centerMinY : -Infinity;
    var maxY = typeof limits.centerMaxY === 'number' ? limits.centerMaxY : Infinity;
    // Deterministic fallback for an inverted merchant configuration —
    // collapse to the single valid point (loadProductConfig() also
    // cross-validates this before it ever reaches here; defense in depth).
    if (minX > maxX) {
      var mx = (minX + maxX) / 2;
      minX = maxX = mx;
    }
    if (minY > maxY) {
      var my = (minY + maxY) / 2;
      minY = maxY = my;
    }
    return {
      x: Math.max(minX, Math.min(maxX, center.x)),
      y: Math.max(minY, Math.min(maxY, center.y))
    };
  }

  /*
    Center <-> legacy top-left convention adapter. `photoAspect` uses the
    SAME convention already established everywhere else in this file
    (width/height — see computeDefaultPhotoTransform/buildMaskedPhotoLayer:
    normalized photoHeight = scale / photoAspect). Deterministic, reversible
    (topLeftToCenter(centerToTopLeft(cx,cy,s,a).x, ..., s, a) reproduces
    cx/cy exactly). Used by ever-leaf-personalizer.js at exactly two
    points: deriving the legacy `_ever_leaf_photo_x/y` cart properties from
    Center state, and (chained into scene-space) at the 3 render calls —
    never to define clamping, which stays purely Center/Scale-space.
  */
  function centerToTopLeft(centerX, centerY, scale, photoAspect) {
    var aspect = photoAspect || 1;
    var height = scale / aspect;
    return { x: centerX - scale / 2, y: centerY - height / 2 };
  }

  function topLeftToCenter(x, y, scale, photoAspect) {
    var aspect = photoAspect || 1;
    var height = scale / aspect;
    return { centerX: x + scale / 2, centerY: y + height / 2 };
  }

  /*
    Pass 3A targeted correction — restores a SAFE Custom Pivot zoom mode on
    top of the Center model, without resurrecting the retired
    computePivotPreservingScale()/mask-cover coupling that caused the
    original drift. Given the CURRENT center/scale, a NEW (already-clamped)
    scale, and a fixed Pivot point P (same normalized Personalization State
    Space as photoCenterX/Y and the old scale-pivot setting) — returns the
    new Center such that P remains visually stationary relative to the
    photo's own content.

    Derivation (worth recording — this is NOT the old top-left pivot
    formula copied over; it's re-derived directly in Center terms, and
    turns out to be exactly aspect-independent, simpler than the old one):
    let fracX/fracY be P's fractional position within the CURRENT drawn
    photo rect (top-left + width/height). Solving
    P = topLeft1 + frac*size1 for frac, then P = topLeft2 + frac*size2 for
    the new topLeft2, converting both through centerToTopLeft/
    topLeftToCenter, the width/height (and therefore aspect) terms cancel
    completely, leaving:
      ratio = newScale / oldScale
      newCenter = P + (oldCenter - P) * ratio
    i.e. literally "scale the vector from the pivot to the current center by
    the scale ratio, keeping the pivot fixed" — the standard 2D zoom-toward-
    a-point transform, applied directly to Center. When newScale === oldScale
    (already handled by the caller BEFORE this is ever invoked — see
    ever-leaf-personalizer.js's resolveZoomTransform), ratio is exactly 1
    and this is a mathematical identity (newCenter === oldCenter) — the
    "complete no-op at scale bounds" guarantee holds by construction, not
    by a separate epsilon check inside this function.
  */
  function computePivotPreservingCenter(centerX, centerY, oldScale, newScale, pivotX, pivotY) {
    var ratio = newScale / oldScale;
    return {
      centerX: pivotX + (centerX - pivotX) * ratio,
      centerY: pivotY + (centerY - pivotY) * ratio
    };
  }

  function drawBack(ctx, canvasSize, options) {
    options = options || {};
    if (options.visible === false) return;
    ctx.save();
    ctx.globalAlpha = options.opacity != null ? options.opacity : 1;
    ctx.fillStyle = options.color || '#ffffff';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.restore();
  }

  function drawLeafBase(ctx, leafBaseImage, canvasSize, options) {
    options = options || {};
    if (options.visible === false) return;
    ctx.save();
    ctx.globalAlpha = options.opacity != null ? options.opacity : 1;
    ctx.drawImage(leafBaseImage, 0, 0, canvasSize, canvasSize);
    ctx.restore();
  }

  /*
    Photo_Mask.png is fully opaque (alpha=1 everywhere) by design — the mask
    shape is carried in its RGB luminance, not its alpha channel (confirmed
    against the source PSD). This converts that luminance into a real alpha
    stencil: white -> opaque (photo visible), black -> transparent (photo
    hidden), gray -> partial, ready for a destination-in clip.
  */
  function buildMaskAlphaCanvas(maskImage, canvasSize) {
    var canvas = document.createElement('canvas');
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(maskImage, 0, 0, canvasSize, canvasSize);
    var imageData = ctx.getImageData(0, 0, canvasSize, canvasSize);
    var data = imageData.data;
    for (var i = 0; i < data.length; i += 4) {
      var luminance = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = luminance;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /*
    Neutral tonal settings — reproduces plain, untouched luminance grayscale
    exactly (this is what the validated PSD parity comparison used). Every
    field is a deliberate, DEV-tunable calibration knob, never baked in by
    default. Photo opacity is intentionally NOT part of this — it stays a
    separate layer-level setting applied at composite time, never baked into
    these pixels (see Section 12 of the tonal-calibration brief).
  */
  var NEUTRAL_PHOTO_TONAL = { brightness: 0, contrast: 0, gamma: 1, blackPoint: 0, whitePoint: 255 };

  /*
    Standard levels -> gamma -> brightness -> contrast pipeline, applied to a
    single 0..255 luminance value:
      1. Levels: stretch [blackPoint..whitePoint] to [0..255], clamped.
      2. Gamma: v = v^(1/gamma) in 0..1 space (gamma>1 lifts midtones).
      3. Brightness: flat additive offset.
      4. Contrast: scale around the 128 midpoint; contrast=0 is neutral
         (factor 1), +/-100 roughly doubles/zeroes the spread.
    All five neutral values reproduce the input unchanged (verified: with
    NEUTRAL_PHOTO_TONAL, output === input for every value 0..255).
  */
  function applyPhotoTonalCurve(gray, tonal) {
    var blackPoint = tonal.blackPoint != null ? tonal.blackPoint : 0;
    var whitePoint = tonal.whitePoint != null ? tonal.whitePoint : 255;
    var range = Math.max(1, whitePoint - blackPoint);
    var v = (gray - blackPoint) / range;
    v = v < 0 ? 0 : v > 1 ? 1 : v;
    var gamma = tonal.gamma || 1;
    v = Math.pow(v, 1 / gamma);
    v = v * 255;
    v += tonal.brightness || 0;
    var contrastFactor = 1 + (tonal.contrast || 0) / 100;
    v = (v - 128) * contrastFactor + 128;
    return v < 0 ? 0 : v > 255 ? 255 : v;
  }

  /*
    Decodes-equivalent step for an already-decoded customer photo source:
    draws it (optionally downscaled — see `options.maxDimension`, used for the
    interactive-resolution source) and converts to grayscale, then applies
    `options.tonal` (brightness/contrast/gamma/blackPoint/whitePoint — see
    applyPhotoTonalCurve) if given; omitted or NEUTRAL_PHOTO_TONAL reproduces
    plain grayscale exactly, which is what the validated PSD parity
    comparison used (native Canvas Color Burn matched Photoshop's own blend
    almost exactly — mean channel difference ~0.0004 — against unmodified
    grayscale). This is a ONE-TIME pixel-loop cost per upload/tonal-change,
    never run during drag/zoom — see ever-leaf-personalizer.js's
    rebuildPreparedPhoto().
  */
  function prepareGrayscalePhoto(source, options) {
    options = options || {};
    var naturalWidth = source.width || source.naturalWidth;
    var naturalHeight = source.height || source.naturalHeight;
    var width = naturalWidth;
    var height = naturalHeight;
    if (options.maxDimension && Math.max(width, height) > options.maxDimension) {
      // Round only the constrained dimension; derive the other from the
      // ORIGINAL precise ratio so this canvas's own aspect ratio stays as
      // close as possible to the un-downscaled source (defense in depth —
      // callers should also pass the canonical aspect explicitly wherever
      // it matters; see applyPhotoTransform's aspectOverride).
      var originalAspect = naturalWidth / naturalHeight;
      if (width >= height) {
        width = options.maxDimension;
        height = Math.max(1, Math.round(width / originalAspect));
      } else {
        height = options.maxDimension;
        width = Math.max(1, Math.round(height * originalAspect));
      }
    }
    var canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(source, 0, 0, width, height);
    var imageData = ctx.getImageData(0, 0, width, height);
    var data = imageData.data;
    var tonal = options.tonal;
    for (var i = 0; i < data.length; i += 4) {
      var gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (tonal) gray = applyPhotoTonalCurve(gray, tonal);
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /*
    Draws `photoCanvas` onto 2d context `ctx` using normalized transform
    { x, y, scale } (all relative to `canvasSize`), preserving the photo's
    aspect ratio. Scale is expressed as drawn-width / canvasSize.

    `aspectOverride`, when given, is used INSTEAD of deriving the ratio from
    `photoCanvas.width/height`. This matters: the interactive-resolution
    prepared photo is independently downscaled+rounded from the HQ one (see
    prepareGrayscalePhoto), so the two canvases' own pixel dimensions can
    differ in aspect ratio by a fraction of a percent — enough to visibly
    shift the drawn rectangle's edges between the interactive and HQ
    renders. Passing the ONE canonical aspect ratio (computed once from the
    original un-downscaled decoded source — see state.photoAspect in
    ever-leaf-personalizer.js) for both renders eliminates that jump
    entirely; this was the confirmed root cause of the Phase 4 interactive/
    HQ geometry mismatch.
  */
  function applyPhotoTransform(ctx, photoCanvas, transform, canvasSize, aspectOverride) {
    var aspect = aspectOverride || photoCanvas.width / photoCanvas.height;
    var drawWidth = transform.scale * canvasSize;
    var drawHeight = drawWidth / aspect;
    var drawX = transform.x * canvasSize;
    var drawY = transform.y * canvasSize;
    ctx.drawImage(photoCanvas, drawX, drawY, drawWidth, drawHeight);
  }

  function createCanvas(size) {
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    return canvas;
  }

  function cloneCanvas(source) {
    var canvas = document.createElement('canvas');
    canvas.width = source.width;
    canvas.height = source.height;
    canvas.getContext('2d').drawImage(source, 0, 0);
    return canvas;
  }

  /* Clips `sourceCanvas` in place to `maskAlphaCanvas` (from buildMaskAlphaCanvas). */
  function applyPhotoMask(sourceCanvas, maskAlphaCanvas) {
    var ctx = sourceCanvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskAlphaCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height);
    ctx.restore();
    return sourceCanvas;
  }

  /*
    Reused across every interactive render (drag/zoom) instead of allocating
    a fresh 1060x1060 canvas per frame — see Section 13 of the corrective
    brief ("no redundant canvas allocation in the hot path").
  */
  var maskedPhotoScratchCanvas = null;

  /*
    Builds the "masked photo layer": the ALREADY-PREPARED (grayscale +
    tonally prepped, cached once at upload — never re-derived here) photo
    drawn at the current transform onto a transparent canvas, then clipped to
    Photo_Mask's shape via destination-in. No pixel readback happens in this
    function — only drawImage calls, so it's cheap enough for every
    pointermove. Reuses `scratchCanvas` in place when provided (the
    interactive path always passes the module's shared scratch canvas; debug
    stage construction passes none, since those runs are infrequent and want
    independent canvases to display side by side).
  */
  function buildMaskedPhotoLayer(preparedPhoto, transform, maskAlphaCanvas, canvasSize, scratchCanvas, aspectOverride) {
    var canvas = scratchCanvas || createCanvas(canvasSize);
    if (canvas.width !== canvasSize || canvas.height !== canvasSize) {
      canvas.width = canvasSize;
      canvas.height = canvasSize;
    }
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    applyPhotoTransform(ctx, preparedPhoto, transform, canvasSize, aspectOverride);
    if (maskAlphaCanvas) applyPhotoMask(canvas, maskAlphaCanvas);
    return canvas;
  }

  /*
    Draws the canonical Photoshop-order relationship onto `ctx`: Leaf Base
    first (already-present base), THEN the masked photo layer drawn ON TOP
    using `compositeMode` (Color Burn by default) — never the reverse. Used
    both by the real interactive path (drawing straight onto the main
    canvas) and by the Stage 7/8 debug visualizations (drawing onto an
    isolated fresh canvas so the full leaf is actually visible in the
    diagnostic, not just the masked circle on transparency).
  */
  function drawLeafWithMaskedPhoto(ctx, leafBaseImage, maskedPhotoLayer, canvasSize, compositeMode, opacity) {
    // Back (white) first — Leaf Base has intentional reduced alpha in the
    // photo region (by design, see PROJECT brief), so the true backdrop
    // Color Burn sees is Back showing through, not solid leaf color. Debug
    // stages must reflect that same real composited backdrop.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.drawImage(leafBaseImage, 0, 0, canvasSize, canvasSize);
    ctx.save();
    ctx.globalCompositeOperation = compositeMode || 'color-burn';
    ctx.globalAlpha = opacity != null ? opacity : 1;
    ctx.drawImage(maskedPhotoLayer, 0, 0, canvasSize, canvasSize);
    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  var HEBREW_RANGE = /[֐-׿]/;

  /*
    Detects whether `text` should render right-to-left, WITHOUT relying on
    the page's ambient CSS/HTML dir — the Canvas text region is fixed in
    Leaf Local Space and must render identically no matter what direction
    context the surrounding page happens to be in (Section 9). A simple
    Hebrew-range check is sufficient for this phase (name/dedication text is
    short and rarely truly mixed-direction in a way that would need full
    Unicode BiDi resolution).
  */
  function detectTextDirection(text) {
    return HEBREW_RANGE.test(text || '') ? 'rtl' : 'ltr';
  }

  /*
    Runtime Dedication — TWO explicit, independently-sized, independently-
    directioned, independently-POSITIONED lines (PASS 3A FINAL DEDICATION
    POSITIONING MODEL). Position/size/color/blend default to TEXT_CANONICAL
    (the PSD-calibrated reference). `dedication` is `{ line1, line2 }` —
    either may be blank. `options.line1FontSizeRatio`/`line2FontSizeRatio`,
    when provided, are the already-fitted sizes from
    computeDedicationLineFit() (cached by the caller) — this function does
    NOT call measureText itself, keeping it safe for the per-frame
    interactive path.

    Layout model (Section 3-9/15 of the brief — NO shared vertical anchor,
    NO derived gap, NO automatic midpoint/symmetric math): both lines share
    ONE `centerXRatio` only. Y is fully independent per line and per mode.
    IMPORTANT coordinate-space note: this function draws using whatever
    space `options` is already expressed in — in production that is SCENE
    space (see ever-leaf-personalizer.js's sceneTextLayerSettings(), which
    maps Personalization State Space -> Scene Space before this ever runs).
    The merchant-facing "Line 2 Y is anchored from the BOTTOM" concept
    (Section 6-7) is a Personalization State Space idea — by the time a Y
    value reaches THIS function it has already been converted to an
    ordinary from-top ratio (`options.line2YRatio`), exactly once, by the
    caller (Section 15: "1 - normalizedBottom -> ... -> Personalization
    -> Scene mapping ONCE" — that single inversion happens upstream, not
    here, so this function never needs to know which merchant convention a
    Y value originally used):
    - Line 1 uses `line1YSingleRatio` when Line 2 is empty, or
      `line1YTwoRatio` when Line 2 also has content — mode is selected
      live, purely from whether `line2` is currently truthy, never
      cached/sticky.
    - Line 2 uses `line2YRatio`, used whenever Line 2 has content,
      REGARDLESS of Line 1's state (Section 8 Case C — Line 1 empty, Line
      2 has text: Line 2 still renders at its own position; Line 1 is
      never fabricated).
    Changing one line's Y, or its font size, or the other line's content,
    NEVER moves the other line — each Y ratio is read independently and
    used as-is, with no cross-reference between them anywhere below.
  */
  function drawDedicationLayer(ctx, dedication, canvasSize, options) {
    options = Object.assign({}, TEXT_CANONICAL, DEFAULT_LAYER_SETTINGS.text, options || {});
    if (options.visible === false || !dedication) return;
    var line1 = dedication.line1 || '';
    var line2 = dedication.line2 || '';
    if (!line1 && !line2) return;

    var centerXRatio = options.centerXRatio != null ? options.centerXRatio : TEXT_CANONICAL.centerXRatio;
    var line1FontSizeRatio =
      options.line1FontSizeRatio != null ? options.line1FontSizeRatio : TEXT_CANONICAL.line1FontSize / LEAF_LOCAL_SIZE;
    var line2FontSizeRatio =
      options.line2FontSizeRatio != null ? options.line2FontSizeRatio : TEXT_CANONICAL.line2FontSize / LEAF_LOCAL_SIZE;

    var x = centerXRatio * canvasSize;

    ctx.save();
    ctx.globalCompositeOperation = options.blendMode || 'overlay';
    ctx.globalAlpha = options.opacity != null ? options.opacity : 0.8;
    ctx.fillStyle = options.color || '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (line1) {
      var line1YRatio = line2
        ? (options.line1YTwoRatio != null ? options.line1YTwoRatio : TEXT_CANONICAL.line1YTwoRatio)
        : (options.line1YSingleRatio != null ? options.line1YSingleRatio : TEXT_CANONICAL.line1YSingleRatio);
      ctx.font = (options.fontWeight || 'bold') + ' ' + line1FontSizeRatio * canvasSize + 'px ' + (options.fontFamily || 'sans-serif');
      ctx.direction = detectTextDirection(line1);
      ctx.fillText(line1, x, line1YRatio * canvasSize);
    }
    if (line2) {
      var line2YRatio = options.line2YRatio != null ? options.line2YRatio : TEXT_CANONICAL.line2YRatio;
      ctx.font = (options.fontWeight || 'bold') + ' ' + line2FontSizeRatio * canvasSize + 'px ' + (options.fontFamily || 'sans-serif');
      ctx.direction = detectTextDirection(line2);
      ctx.fillText(line2, x, line2YRatio * canvasSize);
    }

    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  /* Fixed highlight/light overlay — canonical source is Light.png (from the PSD's Lights layer). */
  function drawLightLayer(ctx, lightImage, canvasSize, options) {
    options = Object.assign({}, DEFAULT_LAYER_SETTINGS.light, options || {});
    if (options.visible === false) return;
    ctx.save();
    ctx.globalCompositeOperation = options.blendMode || 'overlay';
    ctx.globalAlpha = options.opacity != null ? options.opacity : 0.471;
    ctx.drawImage(lightImage, 0, 0, canvasSize, canvasSize);
    ctx.restore();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  /*
    Builds a resolution-specific render context: a pre-composited, reusable
    "Back" backdrop canvas, a mask-alpha stencil scaled to that same size,
    and a reusable scratch canvas for the masked photo layer — all at
    `size`. This is the two-tier (interactive vs. HQ) caching architecture:
    build ONE context per resolution (on mount, and again only if the
    interactive display size actually changes) and reuse it for every
    subsequent interactive frame at that resolution.

    PASS 3C — Leaf Base (Blur/Sharp) is NOT pre-composited/pre-BLENDED into
    this cached backdrop. Their relative WEIGHTS change continuously during
    a viewport transition (see getLampLayerWeights() in
    ever-leaf-personalizer.js), so baking a single blended snapshot in here
    would go stale on every animation frame; the weighted blend itself is
    still done live, in renderInteractive() below, directly on top of this
    same cached "Back" canvas. Back itself still never changes, so caching
    just that layer remains a real, valid optimization.

    ZOOM PERFORMANCE REGRESSION AUDIT (2026-09-24) — Blur/Sharp/Light are,
    however, pre-SCALED to `size` here, exactly like maskAlphaAtSize already
    was — this is independent of the live weight blend above (the blend
    still reads fresh `lampWeights`/`globalAlpha` every frame; only WHICH
    bitmap resolution it draws from is cached). Previously these three were
    stored at their raw, full native source resolution and redrawn with a
    fresh drawImage-driven downscale EVERY interactive frame (every
    rAF-coalesced pointermove/pinch/zoom tick) — cheap at the historical
    2048x2048 scene asset resolution, but the 4096 Scene Resolution
    Migration plus the merchant's real Photo Mask/Lights Overlay/Lamp Sharp
    and Blur Content selections (both dated the same day as the recent
    camera/Model-B passes) made these layers 4096x4096, i.e. a same-frame
    ~2-4x-per-axis downscale done from scratch on every single gesture
    frame regardless of Close-Up/Full Lamp mode or whether the lamp/light
    layers had changed at all — a real, source-verified (see
    scratchpad/zoom-perf-audit/) per-frame cost increase, not a
    hypothetical one. This cache invalidates exactly when the rest of this
    context does (workingSize or active variant Scene change — see
    ensureInteractiveContext() in ever-leaf-personalizer.js), never per
    frame, so it cannot go stale the way a pre-BLENDED snapshot would.
  */
  // FINAL INTERACTIVE PERFORMANCE PASS — the material-specific (Lamp Blur/
  // Sharp) half of createRenderContext(), factored out so
  // updateRenderContextMaterial() below can rebuild ONLY this half on a
  // material switch, sharing this one implementation with the full build so
  // the two can never drift out of sync with each other.
  function buildLampCanvasesAtSize(assets, size) {
    var lampBlurAtSize = createCanvas(size);
    lampBlurAtSize.getContext('2d').drawImage(assets.leafBase, 0, 0, size, size);

    var sharpSource = assets.leafBaseSharp || assets.leafBase;
    var lampSharpAtSize = sharpSource === assets.leafBase ? lampBlurAtSize : createCanvas(size);
    if (lampSharpAtSize !== lampBlurAtSize) {
      lampSharpAtSize.getContext('2d').drawImage(sharpSource, 0, 0, size, size);
    }
    return { lampBlurImage: lampBlurAtSize, lampSharpImage: lampSharpAtSize };
  }

  function createRenderContext(assets, size) {
    var backdrop = createCanvas(size);
    var backdropCtx = backdrop.getContext('2d');
    drawBack(backdropCtx, size, DEFAULT_LAYER_SETTINGS.back);

    var maskAlphaAtSize = createCanvas(size);
    maskAlphaAtSize.getContext('2d').drawImage(assets.maskAlpha, 0, 0, size, size);

    var lampCanvases = buildLampCanvasesAtSize(assets, size);

    var lightAtSize = createCanvas(size);
    lightAtSize.getContext('2d').drawImage(assets.light, 0, 0, size, size);

    return {
      size: size,
      backdropCanvas: backdrop,
      maskAlphaCanvas: maskAlphaAtSize,
      maskedPhotoScratch: createCanvas(size),
      lightImage: lightAtSize,
      lampBlurImage: lampCanvases.lampBlurImage,
      lampSharpImage: lampCanvases.lampSharpImage
    };
  }

  /*
    FINAL INTERACTIVE PERFORMANCE PASS — Material switch root cause. Photo
    Mask and Lights Overlay are shared by EVERY variant's Scene (PHASE 45B —
    "the two 2048x2048 scene assets shared by EVERY variant's Scene"); only
    Lamp Blur/Sharp are material-specific. Before this pass, a material
    switch (a genuine `assets.leafBase`/`leafBaseSharp` identity change)
    went through the SAME createRenderContext() a workingSize change does —
    rebuilding the backdrop, the Mask, AND the Lights Overlay too, even
    though none of those three actually depend on which material is active.
    That needlessly re-downscaled two more 4096px sources on every single
    material click. This function rebuilds ONLY the two Lamp canvases
    (sharing buildLampCanvasesAtSize() with createRenderContext() itself, so
    they can never compute a different result) and returns a NEW context
    object that reuses the EXISTING backdrop/maskAlpha/light/scratch
    canvases by reference — never mutates the caller's previous context in
    place, so a stale reference held elsewhere can never observe a half-
    updated context.
  */
  function updateRenderContextMaterial(context, assets) {
    var lampCanvases = buildLampCanvasesAtSize(assets, context.size);
    return {
      size: context.size,
      backdropCanvas: context.backdropCanvas,
      maskAlphaCanvas: context.maskAlphaCanvas,
      maskedPhotoScratch: context.maskedPhotoScratch,
      lightImage: context.lightImage,
      lampBlurImage: lampCanvases.lampBlurImage,
      lampSharpImage: lampCanvases.lampSharpImage
    };
  }

  /*
    PASS 3C — real dual-layer Blur/Sharp compositing (Section 19/22 of the
    brief). Gray Blur and Gray Sharp are two perfectly registered layers of
    the identical lamp geometry (same 2048x2048 origin — audited; both
    fully opaque, no transparency in either asset). The mathematically
    correct way to crossfade two full-frame opaque images with
    complementary weights via ordinary sequential 'source-over' canvas
    compositing is: draw the FIRST image at full opacity (establishing it
    as the entire visible base), then draw the SECOND on top at exactly
    `sharpWeight` opacity — the result is the true linear blend
    `Blur*(1-sharpWeight) + Sharp*sharpWeight`, with no brightness pulse,
    no double-alpha darkening, and no ghosting. (Sequentially weighting
    BOTH draws — e.g. Blur at `blurWeight` AND Sharp at `sharpWeight` — does
    NOT produce a linear blend when compositing multiple semi-transparent
    'source-over' draws in a row; it was verified analytically before
    implementation and deliberately avoided here.) Blur is skipped
    entirely once `sharpWeight` reaches 1 (Sharp, being fully opaque,
    completely covers it anyway) — a pure performance no-op, never a
    visual difference, safe specifically because both assets are known
    fully opaque.
  */
  function drawLampLayers(ctx, blurImage, sharpImage, canvasSize, lampWeights, leafBaseSettings) {
    var settings = leafBaseSettings || DEFAULT_LAYER_SETTINGS.leafBase;
    if (settings.visible === false) return;
    var baseOpacity = settings.opacity != null ? settings.opacity : 1;
    var weights = lampWeights || DEFAULT_LAMP_WEIGHTS;
    var sharpWeight = typeof weights.sharpWeight === 'number' && isFinite(weights.sharpWeight) ? Math.max(0, Math.min(1, weights.sharpWeight)) : 0;
    if (sharpWeight < 1) {
      drawLeafBase(ctx, blurImage, canvasSize, { opacity: baseOpacity });
    }
    if (sharpWeight > 0 && sharpImage) {
      drawLeafBase(ctx, sharpImage, canvasSize, { opacity: baseOpacity * sharpWeight });
    }
  }

  /*
    The lightweight interactive hot path (Section 24 of the performance
    corrective brief). Zero getImageData/putImageData, zero new canvas
    allocation, zero debug work — only drawImage/compositing calls against a
    pre-built `renderContext` (see createRenderContext). This is what runs on
    every rAF-coalesced pointermove/slider-input frame.

    photoState: { preparedPhotoSource, transform: {x,y,scale}, dedication: {line1,line2}, layers }
    preparedPhotoSource here should be the INTERACTIVE (possibly downscaled)
    prepared photo, not the full HQ one — see prepareGrayscalePhoto's
    `maxDimension` option.
  */
  function renderInteractive(targetCanvas, renderContext, photoState) {
    var size = renderContext.size;
    targetCanvas.width = size;
    targetCanvas.height = size;
    var ctx = targetCanvas.getContext('2d');
    var layers = (photoState && photoState.layers) || {};

    ctx.drawImage(renderContext.backdropCanvas, 0, 0);
    drawLampLayers(
      ctx,
      renderContext.lampBlurImage,
      renderContext.lampSharpImage,
      size,
      photoState && photoState.lampWeights,
      Object.assign({}, DEFAULT_LAYER_SETTINGS.leafBase, layers.leafBase)
    );

    if (photoState && photoState.preparedPhotoSource) {
      var photoSettings = Object.assign({}, DEFAULT_LAYER_SETTINGS.photo, layers.photo);
      if (photoSettings.visible !== false) {
        var transform = Object.assign({}, DEFAULT_PHOTO_TRANSFORM, photoState.transform || {});
        var maskedPhotoLayer = buildMaskedPhotoLayer(
          photoState.preparedPhotoSource,
          transform,
          renderContext.maskAlphaCanvas,
          size,
          renderContext.maskedPhotoScratch,
          photoState.photoAspect
        );
        ctx.save();
        ctx.globalCompositeOperation = 'color-burn';
        ctx.globalAlpha = photoSettings.opacity != null ? photoSettings.opacity : 1;
        ctx.drawImage(maskedPhotoLayer, 0, 0, size, size);
        ctx.restore();
      }
    }

    if (!(layers.text && layers.text.visible === false)) {
      drawDedicationLayer(ctx, photoState && photoState.dedication, size, layers.text);
    }
    if (!(layers.light && layers.light.visible === false)) {
      drawLightLayer(ctx, renderContext.lightImage, size, layers.light);
    }

    return targetCanvas;
  }

  /*
    Orchestrates the full close-up layer pipeline in order: Back -> Leaf Base
    -> Grayscale Photo (Color Burn, masked) -> Text (Overlay) -> Light
    (Overlay). Pure function of `state` — no DOM event wiring, no knowledge
    of Mini/FilePond, matching this file's existing architecture.

    Coordinate convention (used consistently by state, this renderer, drag,
    and zoom — do not mix another convention in anywhere):
      photoX, photoY = normalized (0..1) position of the photo's own
                        TOP-LEFT corner in Leaf Local Space (canvasSize x canvasSize).
      photoScale     = drawn photo width / canvasSize (i.e. "how much of the
                        canvas width the photo occupies"); height follows from
                        the photo's own aspect ratio.

    state: {
      canvasSize,               // defaults to LEAF_LOCAL_SIZE (1060)
      assets,                   // result of loadPersonalizerAssets() (includes cached maskAlpha/maskBounds)
      preparedPhotoSource,      // cached ONCE per upload via prepareGrayscalePhoto() — the hot path never re-derives this
      customerSource,           // original decoded photo, kept only for the Stage 1 "Original" debug preview
      transform: { x, y, scale }, // normalized, see convention above; defaults to DEFAULT_PHOTO_TRANSFORM
      dedication,               // { line1, line2 } — either may be blank
      layers,                   // per-layer { visible, opacity, blendMode, ... } overrides
      debug: {                  // optional — all debug-only, no effect on the real pipeline or its performance
        stages,                 // array this function PUSHES {label, canvas} onto, one per Section 5 stage
        compositeMode,          // 'color-burn' (default) or 'source-over' — quality-gate toggle
        disableText,            // true skips the Text layer entirely
        disableLight            // true skips the Light layer entirely
      }
    }

    Performance contract: when `state.debug` is omitted (the interactive
    drag/zoom path), this function does zero getImageData/putImageData calls
    and allocates zero new canvases — it reuses the shared scratch canvas and
    only issues drawImage/compositing calls. All pixel-level work (grayscale,
    tonal prep, mask luminance extraction) already happened once, earlier, in
    prepareGrayscalePhoto()/loadPersonalizerAssets(). Debug stage generation
    (extra canvases, isolated comparisons) only runs when the caller actually
    asks for it via `state.debug.stages`.
  */
  function renderPersonalizedLeaf(targetCanvas, state) {
    state = state || {};
    var canvasSize = state.canvasSize || LEAF_LOCAL_SIZE;
    targetCanvas.width = canvasSize;
    targetCanvas.height = canvasSize;
    var ctx = targetCanvas.getContext('2d');
    var layers = state.layers || {};
    var assets = state.assets;
    var debug = state.debug;
    var stages = debug && debug.stages;

    function stage(label, canvas) {
      if (stages) stages.push({ label: label, canvas: cloneCanvas(canvas) });
    }

    ctx.clearRect(0, 0, canvasSize, canvasSize);
    drawBack(ctx, canvasSize, Object.assign({}, DEFAULT_LAYER_SETTINGS.back, layers.back));

    if (!assets) return targetCanvas;

    // Canonical order (authoritative): Back -> Leaf Base -> masked Photo
    // drawn ON TOP of Leaf Base using Color Burn -> Text -> Light. Leaf Base
    // is drawn once (as a Blur/Sharp weighted pair — see drawLampLayers()'s
    // own header for the crossfade math), directly onto the real canvas —
    // never duplicated into a side canvas and never composited "under" an
    // already-burned photo.
    drawLampLayers(ctx, assets.leafBase, assets.leafBaseSharp, canvasSize, state.lampWeights, Object.assign({}, DEFAULT_LAYER_SETTINGS.leafBase, layers.leafBase));
    if (stages) stage('6. Leaf Base', targetCanvas);

    if (stages) {
      // Stage 4: Photo_Mask visualization — diagnostic only, drawn onto its
      // own opaque canvas so it's actually visible (never drawn onto the
      // real composite — the mask is data/stencil only, per spec).
      var maskPreview = createCanvas(canvasSize);
      var maskPreviewCtx = maskPreview.getContext('2d');
      maskPreviewCtx.fillStyle = '#000';
      maskPreviewCtx.fillRect(0, 0, canvasSize, canvasSize);
      if (assets.maskAlpha) maskPreviewCtx.drawImage(assets.maskAlpha, 0, 0);
      stage('4. Photo_Mask (visualization only)', maskPreview);

      if (state.customerSource) {
        var originalOnly = document.createElement('canvas');
        originalOnly.width = state.customerSource.width || state.customerSource.naturalWidth;
        originalOnly.height = state.customerSource.height || state.customerSource.naturalHeight;
        originalOnly
          .getContext('2d')
          .drawImage(state.customerSource, 0, 0, originalOnly.width, originalOnly.height);
        stage('1. Original', originalOnly);
      }
    }

    var photoSettings = Object.assign({}, DEFAULT_LAYER_SETTINGS.photo, layers.photo);
    if (photoSettings.visible !== false && state.preparedPhotoSource) {
      var transform = Object.assign({}, DEFAULT_PHOTO_TRANSFORM, state.transform || {});
      var compositeMode = (debug && debug.compositeMode) || 'color-burn';
      var photoOpacity = photoSettings.opacity != null ? photoSettings.opacity : 1;

      if (stages) stage('2. Grayscale', state.preparedPhotoSource);

      if (stages) {
        var transformedOnly = createCanvas(canvasSize);
        applyPhotoTransform(transformedOnly.getContext('2d'), state.preparedPhotoSource, transform, canvasSize, state.photoAspect);
        stage('3. Transformed Photo (no leaf/mask)', transformedOnly);

        var maskedOnly = cloneCanvas(transformedOnly);
        if (assets.maskAlpha) applyPhotoMask(maskedOnly, assets.maskAlpha);
        stage('5. Masked Photo (no leaf/burn)', maskedOnly);

        // Full Leaf Base + masked photo, isolated on its own canvas so both
        // stages show the COMPLETE leaf for direct visual comparison — not
        // just the circular mask region on transparency.
        var sourceOverPreview = createCanvas(canvasSize);
        drawLeafWithMaskedPhoto(sourceOverPreview.getContext('2d'), assets.leafBase, maskedOnly, canvasSize, 'source-over', photoOpacity);
        stage('7. FULL Leaf + Masked Photo (source-over)', sourceOverPreview);

        var colorBurnPreview = createCanvas(canvasSize);
        drawLeafWithMaskedPhoto(colorBurnPreview.getContext('2d'), assets.leafBase, maskedOnly, canvasSize, 'color-burn', photoOpacity);
        stage('8. FULL Leaf + Masked Photo (color-burn)', colorBurnPreview);
      }

      // Interactive path: reuse the shared scratch canvas, no allocation,
      // no pixel readback — transform + mask are pure drawImage/compositing.
      var maskedPhotoLayer = buildMaskedPhotoLayer(
        state.preparedPhotoSource,
        transform,
        assets.maskAlpha,
        canvasSize,
        maskedPhotoScratchCanvas || (maskedPhotoScratchCanvas = createCanvas(canvasSize)),
        state.photoAspect
      );

      // Leaf Base is already on `ctx` from drawLeafBase() above — draw ONLY
      // the masked photo layer on top of it with Color Burn. Matches the
      // canonical order exactly: Leaf Base below, Photo Color Burn above.
      ctx.save();
      ctx.globalCompositeOperation = compositeMode;
      ctx.globalAlpha = photoOpacity;
      ctx.drawImage(maskedPhotoLayer, 0, 0, canvasSize, canvasSize);
      ctx.restore();
    }

    if (!(debug && debug.disableText)) {
      drawDedicationLayer(ctx, state.dedication, canvasSize, layers.text);
    }
    if (!(debug && debug.disableLight)) {
      drawLightLayer(ctx, assets.light, canvasSize, layers.light);
    }

    if (stages) stage('9. Final Composite', targetCanvas);

    return targetCanvas;
  }

  window.EverLeafImageRenderer = {
    renderOriginal: renderOriginal,
    renderCustomerImage: renderCustomerImage,
    decodeFileToSource: decodeFileToSource,
    releaseSource: releaseSource,
    loadPersonalizerAssets: loadPersonalizerAssets,
    loadPersonalizerAssetsFromImages: loadPersonalizerAssetsFromImages,
    computeDefaultPhotoTransform: computeDefaultPhotoTransform,
    clampScaleValue: clampScaleValue,
    clampCenterValue: clampCenterValue,
    centerToTopLeft: centerToTopLeft,
    topLeftToCenter: topLeftToCenter,
    computePivotPreservingCenter: computePivotPreservingCenter,
    prepareGrayscalePhoto: prepareGrayscalePhoto,
    NEUTRAL_PHOTO_TONAL: NEUTRAL_PHOTO_TONAL,
    createRenderContext: createRenderContext,
    updateRenderContextMaterial: updateRenderContextMaterial,
    renderInteractive: renderInteractive,
    renderPersonalizedLeaf: renderPersonalizedLeaf,
    computeDedicationLineFit: computeDedicationLineFit,
    detectTextDirection: detectTextDirection,
    TEXT_CANONICAL: TEXT_CANONICAL,
    LEAF_LOCAL_SIZE: LEAF_LOCAL_SIZE,
    DEFAULT_PHOTO_TRANSFORM: DEFAULT_PHOTO_TRANSFORM,
    DEFAULT_PHOTO_TRANSFORM_CENTER: DEFAULT_PHOTO_TRANSFORM_CENTER
  };
})();
